import { useGate } from 'statsig-react';
import { gql } from '@soundxyz/gql-string';
import { DateUtils } from '@soundxyz/utils/date';
import { useAuth } from '@context/auth/base';
import type { OnData } from '@graphql/graphql-ws';
import { useSubscription } from '@graphql/graphql-ws';
import {
  getFragment,
  NewGlobalUserNotificationDocument,
  NewUserNotificationDocument,
  PresaveDropStartedNotifFragmentDoc,
  ReleasePresavedNotifFragmentDoc,
  useInfiniteQuery,
  UserNotificationsHookDocument,
  ViralPresaveNotifFragmentDoc,
} from '@graphql/reactQuery';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY, FEATURE_GATES } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  subscription NewUserNotification {
    notificationUpdates: userNotificationUpdates {
      notificationTimestamp
    }
  }

  subscription NewGlobalUserNotification {
    notificationUpdates: globalUserNotificationUpdates {
      notificationTimestamp
    }
  }

  query UserNotificationsHook($pagination: CursorConnectionArgs!) {
    userNotifications(pagination: $pagination) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          __typename
          id
          timestamp
          ... on SubscribeToEmailCTA {
            id
          }
          ... on GoldenEggWon {
            ...GoldenEggWinnerNotif
          }
          ... on NewFollower {
            ...NewFollowerNotif
          }
          ... on PresaleStarted {
            ...PresaleArtistDropNotif
          }
          ... on PublicSaleStarted {
            ...ArtistDropNotif
          }
          ... on ReferralReceivedNotification {
            ...ReferralReceivedNotif
          }
          ... on ReleaseMinted {
            ...ReleaseMintedNotif
          }
          ... on ViralMintsReached {
            ...ViralMintNotif
          }
          ... on NewPostComment {
            ...NewPostCommentNotif
          }
          ... on NewPostUpvote {
            ...PostUpvotedNotif
          }
          ... on ArtistReleasePosted {
            ...ArtistReleasePostedNotif
          }
          ... on NewCommentOnRelease {
            ...NewCommentOnReleaseNotif
          }
          ... on NewPostCommentReply {
            ...NewPostCommentReplyNotif
          }
          ... on NewPostCommentUpvote {
            ...NewPostCommentUpvoteNotif
          }
          ... on PlaylistPosted {
            ...PlaylistPostedNotif
          }
          ... on MentionedInPost {
            ...MentionedInPostNotif
          }
          ... on MentionedInPostComment {
            ...MentionedInPostCommentNotif
          }
          ... on MentionedInPostCommentReply {
            ...MentionedInPostCommentReplyNotif
          }
          ... on NewRepost {
            ...RepostNotificationInfo
          }
          ... on PresaveDropStarted {
            ...PresaveDropStartedNotif
          }
          ... on PresaveGiftReceived {
            ...PresaveGiftReceivedNotif
          }
          ... on ViralPresave {
            ...ViralPresaveNotif
          }
          ... on ReleasePresaved {
            ...ReleasePresavedNotif
          }
          ... on NewChannelMember {
            ...NewChannelMemberNotif
          }
          ... on NewChannelPost {
            ...NewChannelPostNotif
          }
        }
      }
    }
  }
`);

export function useUserNotifications() {
  const { authUser } = useAuth();
  const authUserId = authUser?.id;

  const { value: enableSpotifyPresave } = useGate(FEATURE_GATES.USE_SPOTIFY_PRESAVE);

  const {
    orderedList: data,
    isLoading,
    isFetchingNextPage,
    loadMoreNextPage: loadMore,
    hasNextPage = false,
    isInitialLoading,
    refetch,
  } = useInfiniteQuery(UserNotificationsHookDocument, {
    getNextPageParam({ data: { userNotifications } }) {
      return (
        userNotifications?.pageInfo.hasNextPage && {
          after: userNotifications.pageInfo.endCursor,
        }
      );
    },
    list({ userNotifications }) {
      return userNotifications?.edges
        .filter(({ node }) => {
          // If feature flag is OFF we filter out any presave related notification
          // This is temporary and once we remove the FF this IF will be removed as well
          if (
            (node.__typename === 'PresaveDropStarted' ||
              node.__typename === 'PresaveGiftReceived' ||
              node.__typename === 'ViralPresave' ||
              node.__typename === 'ReleasePresaved') &&
            !enableSpotifyPresave
          ) {
            return false;
          }

          // Filter out releases with spotifyPresaveCampaign turned OFF or public sale started or Artist owner of the release
          if (node.__typename === 'ViralPresave') {
            const { presaveRelease } = getFragment(ViralPresaveNotifFragmentDoc, node);

            const isDropLive =
              !!presaveRelease.mintStartTime &&
              DateUtils.getUnixTime(new Date()) >= presaveRelease.mintStartTime;

            const isOwner = authUser?.artist?.id === presaveRelease.artist.id;

            if (isDropLive || !presaveRelease.spotifyPresaveCampaign || isOwner) {
              return false;
            }
          }

          if (node.__typename === 'PresaveDropStarted') {
            const { presaveRelease } = getFragment(PresaveDropStartedNotifFragmentDoc, node);

            const isDropLive =
              !!presaveRelease.mintStartTime &&
              DateUtils.getUnixTime(new Date()) >= presaveRelease.mintStartTime;

            if (isDropLive || !presaveRelease.spotifyPresaveCampaign) {
              return false;
            }
          }

          if (node.__typename === 'ReleasePresaved') {
            const { featuredUsers, numUsers } = getFragment(ReleasePresavedNotifFragmentDoc, node);

            if (numUsers === 0 || featuredUsers.length <= 0) {
              return false;
            }
          }

          return true;
        })
        .map(({ node }) => node);
    },
    uniq({ id }) {
      return id;
    },
    filterQueryKey: {
      authUserId,
    },
    enabled: !!authUserId,
    variables({ pageParam }) {
      const after = pageParam?.after || null;
      return {
        pagination: {
          first: after == null ? 20 : 15,
          after,
        },
      };
    },
    // Don't refetch on window focus to make sure we don't refetch and have the pop up button visible
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  return {
    data,
    isLoading,
    isInitialLoading,
    isFetchingNextPage,
    loadMore,
    hasNextPage,
    refetch,
  };
}

export function useUserNotificationSubscriptions({
  onNewGlobalUserNotificationSubscribeData,
  onNewUserNotificationSubscribeData,
}: {
  onNewGlobalUserNotificationSubscribeData: OnData<typeof NewGlobalUserNotificationDocument>;
  onNewUserNotificationSubscribeData: OnData<typeof NewUserNotificationDocument>;
}) {
  const { isAuthenticatedWithAPI } = useAuth();
  useSubscription({
    query: NewUserNotificationDocument,
    enabled: !!isAuthenticatedWithAPI,
    onData: onNewUserNotificationSubscribeData,
  });

  useSubscription({
    query: NewGlobalUserNotificationDocument,
    enabled: !!isAuthenticatedWithAPI,
    onData: onNewGlobalUserNotificationSubscribeData,
  });
}
